import React, { Component } from "react";
import { connect } from "react-redux";
import { FormData } from "Shared/Form";
import { analyticsFEA } from "src/AnalyticsFEA";
import { ApplicationStore } from "src/store";
import {
    continueCreditAppAction,
    goBackCreditAppAction,
    setCosignerQuestion,
    setCreditApplicationId,
} from "src/store/actions/creditApp/base";
import { openSubModal } from "src/store/actions/onlineShopper/subModal";
import { patchDealPerson } from "src/store/dataService/actions/deal";
import { getVehiclesArrayForFEA } from "src/store/selectors/vehicles";
import { logError } from "src/utils/logger";
import { Deal } from "Types/api/Deal";
import { DealState } from "Types/state";
import { ActionSteps } from "./enums";
import { submitCreditApp } from "./submitCreditApp";

interface Props {
    account: any;
    currentDeal: any;
    currentStep: number;
    deal: DealState;
    vehicles: any[];
    onBack: any;
    onNext: any;
    resetCreditApp: any;
    openUnableToSubmitCreditAppApologyModal: () => void;
    setCosigner: any;
    setApplicationId: any;
    tradeIn: any;
    updateDealPerson: any;
    annualEstimatedIncome: number;
}

interface State {
    formData: FormData[];
}

const StepWrapper = (CreditApp: any, goBack: () => void) =>
    connect(
        ({
            account,
            creditApp: {
                base: { currentStep },
            },
            currentDeal,
            deal,
            tradeIn,
            userInfo,
            vehicles,
        }: ApplicationStore) => ({
            account,
            annualEstimatedIncome: userInfo.annualEstimatedIncome,
            currentDeal,
            currentStep,
            deal,
            tradeIn,
            vehicles: getVehiclesArrayForFEA({ vehicles } as ApplicationStore),
        }),
        (dispatch: any) => ({
            onBack: () => dispatch(goBackCreditAppAction()),
            onNext: () => dispatch(continueCreditAppAction()),
            openUnableToSubmitCreditAppApologyModal: () =>
                dispatch(openSubModal({ modalType: "unableToSubmitCreditAppApologyModal" })),

            setCosigner: (data: { hasCosigner: boolean }) => dispatch(setCosignerQuestion(data)),
            setCreditApplicationId: (data: { application_id: string }) => dispatch(setCreditApplicationId(data)),
            updateDealPerson: (uuid: string, dealPerson: Deal["person"]) => dispatch(patchDealPerson(uuid, dealPerson)),
        })
    )(
        class WithCreditAppState extends Component<Props, State> {
            public defaults: { [step: number]: {} };

            constructor(props: Props) {
                super(props);

                this.state = {
                    formData: [],
                };

                if (!this.props.deal.person) {
                    return;
                }

                const {
                    first_name,
                    last_name,
                    email,
                    zip,
                    street1,
                    state,
                    city,
                    mobile_phone,
                } = this.props.deal.person;

                this.defaults = {
                    0: {
                        emailAddress: email,
                        firstName: first_name,
                        lastName: last_name,
                        primaryPhone: mobile_phone,
                    },
                    2: {
                        address1: street1,
                        city,
                        state,
                        zipCode: zip,
                    },
                    4: {
                        primaryIncome: Math.round(this.props.annualEstimatedIncome / 12), // primaryIncome is per month
                    },
                };
            }

            public render() {
                const { currentStep } = this.props;
                const formData = this.state.formData[currentStep] || this.defaults[currentStep] || {};
                return (
                    <CreditApp
                        {...this.props}
                        onNextClick={this.onNext}
                        onBackClick={this.onBack}
                        formData={formData}
                        onFormChanged={this.onFormChanged}
                        useSameAddress={this.useSameAddress}
                    />
                );
            }

            private onNext = (data: FormData) => {
                const { onNext, deal, vehicles } = this.props;
                const currentStep = this.props.currentStep;

                this.sendAnalyticsFEAEvents(currentStep);

                if (currentStep === ActionSteps.PRIMARY_END) {
                    this.props.setCosigner(data);
                }
                this.setState(
                    ({ formData }) => {
                        formData[currentStep] = data;

                        return {
                            formData,
                        };
                    },
                    async () => {
                        if (currentStep !== ActionSteps.FINAL_STEP) {
                            onNext();
                            return;
                        }
                        try {
                            const currentVehicle = this.props.vehicles.find(
                                (v) => v.vin === this.props.currentDeal.vin
                            );
                            const response = await submitCreditApp(
                                this.state.formData,
                                currentVehicle,
                                this.props.tradeIn,
                                this.props.account.integration_settings.DI_Credit.dealer_id
                            );

                            const result = await response.json();

                            // We're only going to throw if the response is a non 200 or if we're missing the application_id
                            if (!response.ok || !result.application_id) {
                                // eslint-disable-next-line no-throw-literal
                                throw { name: "Non200CreditAppError", code: response.status, responseBody: result };
                            }

                            // At this point we have an application_id so the submission went through
                            // Just log the error so the customer doesn't get an error message
                            if (
                                result.errors &&
                                ((Array.isArray(result.errors) && result.errors.length) ||
                                    Object.keys(result.errors).length)
                            ) {
                                logError("200CreditAppError", result.errors);
                            }

                            const applicationID = result.application_id.toString();

                            analyticsFEA.engine.creditAppComplete(deal && deal.uuid!, vehicles, applicationID);
                            analyticsFEA.engine.hardPullCreditAppComplete(deal && deal.uuid!, vehicles, applicationID);

                            this.props.updateDealPerson(this.props.deal.uuid, {
                                credit_application_id: applicationID,
                            });
                            // We are storing the credit app ID in creditApp and on the person.  Not sure if this will need to continue to happen in the future
                            setCreditApplicationId({
                                application_id: applicationID,
                            });
                            onNext();
                        } catch (e) {
                            this.props.openUnableToSubmitCreditAppApologyModal();

                            if (e.name === "Non200CreditAppError") {
                                logError("Non200CreditAppError", { code: e.code, responseBody: e.responseBody });
                            } else {
                                logError(e);
                            }
                        }
                    }
                );
            };

            private onBack = (data: FormData) => {
                const { onBack } = this.props;

                if (this.props.currentStep === ActionSteps.PRIMARY_START) {
                    goBack();
                    return;
                }

                if (this.props.currentStep === ActionSteps.PRIMARY_END) {
                    this.props.setCosigner(data);
                }

                this.setState(
                    ({ formData }) => {
                        formData[this.props.currentStep] = data;

                        return {
                            formData,
                        };
                    },
                    () => {
                        onBack();
                    }
                );
            };

            private onFormChanged = (data: FormData) => {
                this.setState(({ formData }) => {
                    formData[this.props.currentStep] = data;

                    return {
                        formData,
                    };
                });
            };

            private useSameAddress = (value: { target: { value: boolean } }) => {
                if (!value.target.value) {
                    return;
                }

                this.setState(({ formData }) => {
                    const residence = formData[ActionSteps.RESIDENCE];

                    formData[ActionSteps.COSIGNER_RESIDENCE] = {
                        ...formData[ActionSteps.COSIGNER_RESIDENCE],
                        address1: residence.address1,
                        address2: residence.address2,
                        city: residence.city,
                        state: residence.state,
                        zipCode: residence.zipCode,
                    };

                    return {
                        formData,
                    };
                });
            };

            private sendAnalyticsFEAEvents = (step: number) => {
                switch (step) {
                    case 0:
                        analyticsFEA.engine.creditAppPrimaryPageTwo();
                        break;
                    default:
                    // do nothing
                }
            };
        }
    );

export default StepWrapper;
